// This is a wrapper for the original implementation of the download.
// Purpose is not to spill all related APIs into original summary page and reuse.
// Most of the code is copied and rearranging.

import {
  FILE_VERSION,
  INITIAL_FILE_VERSION,
  KEY_FIGURES_ARRAY,
  SET_WORKCENTRES,
  FILE_FORMATS,
  INITIAL_FILE_FORMAT,
  FILE_NAME,
  FILE_NAME_WITH_LEDGER,
  SET_KEY_FIGURES,
} from 'Features/OperationalPlanner/DownloadDataModal/DownloadData.constants'
import { PLANNING_BOOK_LEDGER } from 'Features/DownloadDataModal/DownloadData.constants'
import {
  useDashboardDownload,
  usePlanPlanningBookLedgerDownload,
  usePlanPlanningBookLedgerKeyFigures,
} from 'Features/OperationalPlanner/DownloadDataModal/DownloadDataModal.data'
import { useDataDownloadSettingsState } from 'Features/OperationalPlanner/DownloadDataModal/DownloadDataModal.state'
import { DataDownloadStateAction } from 'Features/OperationalPlanner/DownloadDataModal/DownloadDataModal.types'
import { DownloadDataModal } from 'Features/OperationalPlanner/DownloadDataModal/DownloadDataModal.view'
import { usePlanBuiltByV2Engine } from 'Common/Charts/StockWaterfall/stockWaterfall.data'
import { getApiErrorMessage } from 'Features/auth/axios-client'
import { format } from 'date-fns'
import { useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'react-hot-toast'
import { useQueryClient } from 'react-query'
import {
  BreadcrumbType,
  OperationalPlanDownloadModalProps,
} from './OperationalPlanDownload.modal.types'
import * as XLSX from 'xlsx'

export const OperationalPlanDownloadModal = ({
  isModalOpen,
  handleModalCancel,
  planId,
  selectedPlanName,
}: OperationalPlanDownloadModalProps) => {
  const queryClient = useQueryClient()
  const [planName, setPlanName] = useState(selectedPlanName ?? 'plan_name')

  // `compression` only on the Planning Book Ledger (US149971): its all-WC
  // xlsx is ~92 MB uncompressed, ~22 MB compressed.
  const onDownloadSuccess = (data: any, compression = false) => {
    const fileName = `${dataDownloadSettingsState.fileName}__${planName}__${
      dataDownloadSettingsState.fileVersion
    }__${format(new Date(), 'yyyyMMdd')}`

    if (dataDownloadSettingsState.fileFormat === 'csv') {
      const url = window.URL.createObjectURL(new Blob([data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `${fileName}.csv`)
      document.body.appendChild(link)
      link.click()
    } else {
      const column_order = data?.column_order
      const keys = Object.keys(column_order)

      keys.sort((A, B) => column_order[A] - column_order[B])
      const reorderColumns = (data: Object[], columns: string[]) => {
        return data.map((row: any) => {
          let newRow: any = {}
          columns.forEach((column) => {
            if (row.hasOwnProperty(column)) {
              newRow[column] = row[column]
            }
          })
          Object.keys(row).forEach((column) => {
            if (!columns.includes(column)) {
              const newKey = Number.isInteger(Number(column))
                ? String(column).slice(0, 4) + '' + String(column).slice(4)
                : column
              newRow[newKey] = row[column]
            }
          })
          return newRow
        })
      }

      const orderedData = reorderColumns(data?.data, keys)

      const workbook = XLSX.utils.book_new()
      const worksheet = XLSX.utils.json_to_sheet(orderedData, { header: keys })

      XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1')
      XLSX.writeFile(
        workbook,
        `${fileName}.xlsx`,
        compression ? { compression: true } : undefined
      )
    }

    toast.success('Download Successful')
  }

  const onDownloadFailure = () => {
    toast.error('Download Failure')
  }

  const { mutate: downloadData } = useDashboardDownload(
    onDownloadSuccess,
    onDownloadFailure
  )
  // The ledger routes answer with their reason (e.g. no work centre has a
  // ledger); show it (US150091).
  const { mutate: downloadLedger } = usePlanPlanningBookLedgerDownload(
    (data) => onDownloadSuccess(data, true),
    (error) => toast.error(getApiErrorMessage(error, 'Download Failure'))
  )

  const [dataDownloadSettingsState, updateDataDownloadSettingsState] =
    useDataDownloadSettingsState({
      settings: {
        fileName: 'Work Centre Outputs',
        fileVersion: INITIAL_FILE_VERSION,
        keyFigures: KEY_FIGURES_ARRAY,
        fileFormat: INITIAL_FILE_FORMAT,
        workcentres: [],
      },
    })

  const updateState = (action: DataDownloadStateAction) => {
    updateDataDownloadSettingsState(action)
  }

  // "Planning Book Ledger": offered only when the plan-level GET answers 200
  // (the plan has a ledger catalogue), and asked only for plans the V2 engine
  // built.
  const { builtByV2Engine } = usePlanBuiltByV2Engine(planId)
  const { data: ledgerKeyFigures, isSuccess: hasLedgerDownload } =
    usePlanPlanningBookLedgerKeyFigures(planId, isModalOpen && builtByV2Engine)
  const ledgerCodes = useMemo(
    () => (ledgerKeyFigures ?? []).map((k) => k.measureCode),
    [ledgerKeyFigures]
  )
  const ledgerLabels = useMemo(
    () =>
      Object.fromEntries(
        (ledgerKeyFigures ?? []).map((k) => [k.measureCode, k.label])
      ),
    [ledgerKeyFigures]
  )
  const isLedgerSelected =
    dataDownloadSettingsState.fileName === PLANNING_BOOK_LEDGER
  // The dialog selects WC codes; the ledger is keyed by work-centre key.
  const workcentreKeys = useRef<Record<string, string[]>>({})

  // Picking the ledger selects all its key figures (codes); leaving it puts
  // back the key figures the other options had.
  const previousFileName = useRef(dataDownloadSettingsState.fileName)
  const keyFiguresBeforeLedger = useRef<string[]>([])
  useEffect(() => {
    const previous = previousFileName.current
    const current = dataDownloadSettingsState.fileName
    previousFileName.current = current
    if (current === PLANNING_BOOK_LEDGER && previous !== PLANNING_BOOK_LEDGER) {
      keyFiguresBeforeLedger.current = dataDownloadSettingsState.keyFigures
      updateState({ type: SET_KEY_FIGURES, payload: ledgerCodes })
    } else if (
      previous === PLANNING_BOOK_LEDGER &&
      current !== PLANNING_BOOK_LEDGER
    ) {
      updateState({
        type: SET_KEY_FIGURES,
        payload: keyFiguresBeforeLedger.current,
      })
    }
  }, [dataDownloadSettingsState.fileName])

  useEffect(() => {
    if (isModalOpen && !selectedPlanName) {
      const nameData: BreadcrumbType | undefined =
        queryClient.getQueryData<BreadcrumbType>(['breadcrumbs', planId, null])

      const planName = nameData ? nameData?.data[planId] : 'plan_name'
      setPlanName(planName)
      updateState({
        type: SET_WORKCENTRES,
        payload: [],
      })
    }
  }, [isModalOpen])

  const handleModalSave = () => {
    if (isLedgerSelected) {
      const keys = dataDownloadSettingsState.workcentres.flatMap(
        (code: string) => workcentreKeys.current[code] ?? []
      )
      downloadLedger({
        planId: planId,
        workcentres: Array.from(new Set(keys)),
        settings: dataDownloadSettingsState,
      })
    } else {
      downloadData({
        planId: planId,
        formSettings: dataDownloadSettingsState,
      })
    }
    handleModalCancel()
  }
  return (
    <DownloadDataModal
      planId={planId}
      isModalOpen={isModalOpen}
      handleModalSave={handleModalSave}
      handleModalCancel={handleModalCancel}
      handleModalClose={handleModalCancel}
      dataDownloadSettingsState={dataDownloadSettingsState}
      updateState={updateState}
      fileNameLabel="File name"
      fileVersionLabel="File version"
      keyFiguresLabel="Key figures"
      heading="Download data"
      fileNames={hasLedgerDownload ? FILE_NAME_WITH_LEDGER : FILE_NAME}
      keyFigureItems={isLedgerSelected ? ledgerCodes : KEY_FIGURES_ARRAY}
      keyFigureLabels={isLedgerSelected ? ledgerLabels : undefined}
      onWorkcentreKeys={(keysByCode) => {
        workcentreKeys.current = keysByCode
      }}
    />
  )
}
